import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

const SLUG = "montessori-sanky";
const H1 = "Montessori-sänky, talosänky & lattiasänky lapselle";
const TITLE = "Montessori-sänky, talosänky & lattiasänky 6 kk–6 v | Montessori24";
const DESC =
  "Matala Montessori-sänky, talosänky ja lattiasänky lapselle 6 kk–6 v — turvallinen ja kestävä. Koivupuu, ilmainen toimitus Suomeen 3–7 pv.";

const FAQS = [
  {
    q: "Mikä on Montessori-sänky?",
    a: "Montessori-sänky on matala lattiasänky, jonka pintapatja on lapsen ulottuvilla. Lapsi voi nousta ja mennä nukkumaan itse — Montessorin ydin: itsenäisyys ja ympäristön hallinta.",
  },
  {
    q: "Mistä iästä lähtien?",
    a: "Sopiva ajankohta vaihtelee lapsen ja perheen mukaan; moni siirtyy pinnasängystä lattiasänkyyn taaperoiässä. Noudata aina sängyn valmistajan omaa ikäsuositusta ja käyttöohjetta, ja huomioi vauvan turvallisen nukkumisen ohjeet (THL / neuvola).",
  },
  {
    q: "Mitä lattiasängyn turvallisuudessa pitää huomioida?",
    a: "Matala korkeus pienentää putoamiskorkeutta, mutta turvallisuus syntyy koko huoneesta: pistorasiat suojaan, huonekalut ja hyllyt kiinni seinään, verhonarut ja pienet esineet pois ulottuvilta. Noudata sängyn ja patjan valmistajan ohjeita sekä vauvaiässä turvallisen nukkumisen suosituksia.",
  },
  {
    q: "Mikä koko?",
    a: "Yleisimmät lastensängyn koot Suomessa ovat 70×140 cm ja 90×200 cm. Tarkista aina mallikohtaiset mitat ja patjasuositus valmistajan tuotetiedoista.",
  },
  {
    q: "Mikä on talosänky ja miten se eroaa Montessori-sängystä?",
    a: "Talosänky on lastensänky, jonka runko muistuttaa pientä taloa: pystyt tolpat ja harjakatto sängyn yläpuolella. Se on käytännössä Montessori-lattiasängyn suosituin muotoilu — matala kehys, johon lapsi nousee itse, ja katto tuo turvallisen majakokemuksen. Tuotekohtaiset mitat, materiaalit ja turvallisuustiedot löydät aina kyseisen tuotteen omalta sivulta.",
  },
  {
    q: "Minkä ikäiselle talosänky sopii?",
    a: "Talosänky sopii samoin kuin muu Montessori-lattiasänky. Ikäsuositus ja sopiva koko vaihtelevat mallin mukaan — tarkista tuotekohtaiset mitat ja ikäsuositus tuotesivulta. Katos toimii myös leikin osana — lapset ripustavat sinne valonauhoja ja verhoja.",
  },
];

export const Route = createFileRoute("/montessori-sanky")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: MontessoriSankyPage,
});

function MontessoriSankyPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Montessori-sängyt"
      title={H1}
      intro="Opas Montessori-lattiasänkyyn ja talosänkyyn: mitä ne ovat, milloin siirtymä kannattaa ja miten huone tehdään turvalliseksi. Sänkymallistomme on valmisteilla — alta löydät oppaan ja muut lastenhuoneen kalusteet."
      kauppaCategory="all"
      match={(t, _vendor, tags) => {
        // Title and tags only. The Shopify vendor field carries the storefront
        // label "Montessori House Bed" on climbing frames, so matching on vendor
        // filled this collection with products that are not beds. A generic
        // "patja" match also pulled in soft-play mat sets.
        const hay = `${t} ${(tags ?? []).join(" ")}`.toLowerCase();
        return /(lastensänky|lattiasänky|talosänky|\bsänky\b|\bsängyn\b|floor\s*bed|house\s*bed|toddler\s*bed)/i.test(hay);
      }}
      relatedChips={[
        { label: "Kaikki säilytys & huone", to: "/kauppa" },
        { label: "IGLU-palikat", to: "/iglu" },
        { label: "Lattiapatja-opas", to: "/montessori-koulu/montessori-lattiapatja" },
        { label: "Kalusteopas", to: "/montessori-koulu/montessori-kalusteet-kotiin" },
      ]}
      body={[
        {
          heading: "Talosänky, lattiasänky vai Montessori-sänky — mikä on ero?",
          paragraphs: [
            "Kaikki kolme tarkoittavat käytännössä samaa asiaa hieman eri painotuksella. Montessori-sänky viittaa pedagogiseen ideaan: lapsi hallitsee omaa nukkumistaan. Lattiasänky kuvaa muotoa — patja on lattian tasalla tai vain muutaman sentin korkeudella. Talosänky on taas suosituin muotoiluvariaatio: matala kehys, jonka päällä on pieni talon katto. Kaikki ovat matalia ja turvallisia, kaikkiin lapsi nousee ja poistuu itsenäisesti.",
            "Talosänky yhdistää matalan Montessori-lattiasängyn idean ja lapselle tärkeän mielikuvitusleikin. Mitat, materiaalit ja ikäsuositus vaihtelevat mallin mukaan, joten tarkista ne aina valmistajan tuotetiedoista.",
          ],
        },
        {
          heading: "Miksi lattiasänky?",
          paragraphs: [
            "Perinteinen pinnasänky on aikuisen työkalu — lapsi otetaan sisään ja ulos. Montessori-lattiasänky kääntää tilanteen: lapsi hallitsee omaa nukkumistaan. Aamulla hän nousee itse ja tulee vanhemman luo; illalla hän menee sänkyyn kun on väsynyt.",
            "Vanhempien palaute on tyypillisesti sama: ensimmäiset viikot vievät sopeutumista, mutta muutaman kuukauden jälkeen aamut ovat rauhallisempia ja iltarutiinit sujuvat itsenäisemmin.",
          ],
        },
        {
          heading: "Näin siirryt pinnasängystä lattiasänkyyn",
          paragraphs: [
            "1) Lapsiturvallista huone: pistorasiat, huonekalujen ankkurointi, portaikot ja pienet esineet.",
            "2) Aseta sänky nurkkaan tai kahden seinän väliin, jottei se liiku eikä lapsi vieri kauas.",
            "3) Aloita päiväunilla — lyhyempi jakso, matalampi kynnys. Yöunet siirtyvät luontevasti perässä.",
            "4) Pidä iltarutiini samana: valot, kirja, hiljainen läsnäolo. Muuttuja on vain sänky.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}